import { Material } from '../../../../types/api/Material';
import RootStore from '../../root-store';
import {
  ChangeMaterialMidBatchParams,
  ValidationResult,
} from './types';
import type { IBin } from '../../../../types/device/DeviceModels';

export const validateMaterialSubstitution = (rootStore: RootStore, params: ChangeMaterialMidBatchParams): ValidationResult => {
  const { batchStore, materialStore, devicesStore } = rootStore;
  const { originalMaterialId, substituteMaterialId, scaleId } = params;

  const runningData = batchStore.running.find(r =>
    r.scales &&
      r.scales[scaleId] &&
      r.scales[scaleId].some((s: any) => String(s.material.itemId) === String(originalMaterialId)),
  );

  if (!runningData) {
    return { isValid: false, error: 'No active batch found for material change' };
  }

  const originalMaterial = materialStore.getMaterialByItemId(originalMaterialId);

  // Try to find substitute material in assigned materials first, then in all materials if not found
  let substituteMaterial = materialStore.getMaterialByItemId(substituteMaterialId);
  if (!substituteMaterial) {
    substituteMaterial = materialStore.getMaterialByItemId(substituteMaterialId, true);
  }

  if (!originalMaterial || !substituteMaterial) {
    return { isValid: false, error: 'Invalid materials specified for substitution' };
  }

  if (originalMaterial.assignment !== substituteMaterial.assignment) {
    return { isValid: false, error: 'Materials must be of the same assignment type (weighed/metered)' };
  }

  const materialDetails = materialStore.materialDetails as Record<string, Material>;
  const originalMaterialDetails = materialDetails[originalMaterial.materialDetailId];
  const substituteMaterialDetails = materialDetails[substituteMaterial.materialDetailId];

  if (!originalMaterialDetails || !substituteMaterialDetails) {
    return { isValid: false, error: 'Material details not found for substitution' };
  }

  const scaleDataIndex = runningData.scales[scaleId].findIndex((s: any) => String(s.material.itemId) === String(originalMaterialId));
  if (scaleDataIndex === -1) {
    return { isValid: false, error: 'Material not found in current batch scale data' };
  }

  const scaleData = runningData.scales[scaleId][scaleDataIndex];
  const scale = devicesStore.getDeviceById(Number(scaleId));
  const originalBin = scaleData.bin;
  let substituteBin = materialStore.findBinForMaterialOnScale(substituteMaterial, Number(scaleId)) as IBin;

  // If no active bin found, check if this material was used in a finished batch on this scale
  // and try to reuse that bin if it's available
  if (!substituteBin) {
    const finishedMaterialEntry = runningData.scales[scaleId].find((material: any) =>
      String(material.material.itemId) === String(substituteMaterialId) && material.isFeedFinish,
    );

    if (finishedMaterialEntry && finishedMaterialEntry.bin) {
      substituteBin = finishedMaterialEntry.bin;
    }
  }

  if (!substituteBin) {
    return { isValid: false, error: `No bin available for material ${substituteMaterial.code} on the same scale` };
  }

  if (!scale) {
    return { isValid: false, error: 'Scale device not found' };
  }

  if (!originalBin) {
    return { isValid: false, error: 'Original bin not found' };
  }

  return {
    isValid: true,
    runningData,
    originalMaterial,
    substituteMaterial,
    originalMaterialDetails,
    substituteMaterialDetails,
    scaleDataIndex,
    scaleData,
    scale,
    originalBin,
    substituteBin,
  };
};
